import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Extension, Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import DetailHeader from '../components/DetailHeader';
import { SUPPORTED_CURRENCY_CODES } from '../constants/currency';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import { createAddonAdmin, fetchAddonAdmin, updateAddonAdmin } from '../services/subscriptions';
import type { CurrencyCode } from '../types';

type AddonCode = 'user_extra' | 'ai_agent_extra' | 'whatsapp_extra';

const defaultForm: {
  code: AddonCode;
  name: string;
  description: string;
  priceAmount: string;
  priceCurrency: CurrencyCode;
  isActive: boolean;
} = {
  code: 'user_extra',
  name: '',
  description: '',
  priceAmount: '',
  priceCurrency: 'BRL',
  isActive: true,
};

export default function AddonFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new' || id === 'undefined';
  const { formatCurrency } = useNumberFormat();
  usePageTitle(isNew ? t('pages:addons.new') : t('pages:addons.editTitle'));

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew || !id) return;
    const loadAddon = async () => {
      try {
        setLoading(true);
        const addon = await fetchAddonAdmin(id as string);
        const priceCurrency = SUPPORTED_CURRENCY_CODES.includes(addon.priceCurrency as CurrencyCode)
          ? (addon.priceCurrency as CurrencyCode)
          : 'BRL';
        setForm({
          code: addon.code,
          name: addon.name || '',
          description: addon.description || '',
          priceAmount: String(addon.priceAmount ?? ''),
          priceCurrency,
          isActive: addon.isActive,
        });
      } catch (err) {
        console.error('Error loading addon:', err);
        setError(t('pages:addons.errors.load'));
      } finally {
        setLoading(false);
      }
    };
    loadAddon();
  }, [id, isNew, t]);

  const breadcrumbs = useMemo(
    () => [
      { label: t('common:menu.dashboard'), to: '/' },
      { label: t('pages:addons.title'), to: '/subscriptions/addons' },
      { label: isNew ? t('pages:addons.new') : t('pages:addons.editTitle') },
    ],
    [isNew, t]
  );

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description,
        price_amount: Number(form.priceAmount || 0),
        price_currency: form.priceCurrency,
        is_active: form.isActive,
      };
      if (isNew) {
        await createAddonAdmin(payload);
      } else {
        await updateAddonAdmin(id as string, payload);
      }
      navigate('/subscriptions/addons');
    } catch (err) {
      console.error('Error saving addon:', err);
      setError(t('pages:addons.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <DetailHeader
          sectionIcon={<Extension />}
          title={t('pages:addons.title')}
          breadcrumbs={breadcrumbs}
        />
        <Typography color="text.secondary">{t('common:common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        sectionIcon={<Extension />}
        title={isNew ? t('pages:addons.new') : t('pages:addons.editTitle')}
        breadcrumbs={breadcrumbs}
        actions={(
          <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        )}
      />
      <Card>
        <CardContent>
          <Stack spacing={3}>
            {error && <Typography color="error">{error}</Typography>}
            <FormControl fullWidth>
              <InputLabel>{t('pages:addons.fields.code')}</InputLabel>
              <Select
                value={form.code}
                label={t('pages:addons.fields.code')}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, code: event.target.value as AddonCode }))
                }
              >
                <MenuItem value="user_extra">{t('pages:addons.codes.user_extra')}</MenuItem>
                <MenuItem value="ai_agent_extra">{t('pages:addons.codes.ai_agent_extra')}</MenuItem>
                <MenuItem value="whatsapp_extra">{t('pages:addons.codes.whatsapp_extra')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('pages:addons.fields.name')}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
            />
            <TextField
              label={t('pages:addons.fields.description')}
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label={t('pages:addons.fields.price')}
              value={form.priceAmount}
              onChange={(event) => setForm((prev) => ({ ...prev, priceAmount: event.target.value }))}
              fullWidth
              helperText={formatCurrency(Number(form.priceAmount || 0), form.priceCurrency, 2)}
            />
            <FormControl fullWidth>
              <InputLabel>{t('pages:addons.fields.currency')}</InputLabel>
              <Select
                value={form.priceCurrency}
                label={t('pages:addons.fields.currency')}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    priceCurrency: event.target.value as CurrencyCode,
                  }))
                }
              >
                {SUPPORTED_CURRENCY_CODES.map((currencyCode) => (
                  <MenuItem key={currencyCode} value={currencyCode}>
                    {currencyCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label={t('pages:addons.fields.isActive')}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
